import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService as FEAuthService } from '@shared/services/auth.service';
import { AuthService as AdminAuthService } from '@admin/shared/services/admin.auth.service';
import { catchError, switchMap, throwError, Observable, BehaviorSubject, filter, take, of } from 'rxjs';
import { TokensPayload } from '@shared/models/auth.model';
import { Router } from '@angular/router';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

class RefreshState {
  isRefreshing = false;
  refreshTokenSubject = new BehaviorSubject<string | null>(null);
  retryCount = 0;
}
const refreshState = new RefreshState();

// Helper to add Authorization header
function addToken<T>(request: HttpRequest<T>, token: string): HttpRequest<T> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

export const unauthErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const currentRoute = router.url;
  let authService: FEAuthService | AdminAuthService;
  if (currentRoute.includes('adminpanel')) {
    authService = inject(AdminAuthService);
  } else {
    authService = inject(FEAuthService);
  }
  let tokens: TokensPayload | null = typeof authService.getToken === 'function' ? authService.getToken() : authService.authTokens;

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      // Logout immediately on 504 Gateway Timeout
      if (err.status === 504) {
        // Use the appropriate auth service (admin or FE) determined above
        if (typeof (authService as any).logout === 'function') {
          (authService as any).logout();
        } else {
          // Fallback: clear storage and navigate to login
          localStorage.clear();
          sessionStorage.clear?.();
           if (currentRoute.includes('adminpanel')) {
            router.navigate(['/adminpanel/login']);
           }else{
             router.navigate(['/login']);
           }
        }
        return throwError(() => err);
      }

      if (!authService.authTokens?.refresh_Token) {
        return throwError(() => err);
      }

      if (err.status !== 401) return throwError(() => err);

      if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);
        return authService
          .refreshToken(
            authService.authTokens.access_Token,
            authService.authTokens.refresh_Token
          )
          .pipe(
            switchMap((newToken) => {
              const { access_Token } = newToken as TokensPayload;
              isRefreshing = false;
              authService.setToken(newToken as TokensPayload);
              refreshTokenSubject.next(access_Token);
              return next(addToken(req, access_Token));
            }),
              catchError((refreshErr) => {
                isRefreshing = false;
                // Clear localStorage and redirect to login
                localStorage.clear();
                // Redirect to login route (adjust path as needed)
                router.navigate(['/login']);
                return throwError(() => refreshErr);
              })
          );
      }

      return refreshTokenSubject.pipe(
        filter((token) => token != null),
        take(1),
        switchMap((accessToken) => {
          if (!accessToken)
            return throwError(() => new Error('Access token is null'));
          return next(addToken(req, accessToken));
        })
      );
    })
  );
};